import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMoney, apiErr, getToken } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, ArrowRightLeft, QrCode, KeyRound, Ban, CheckCircle2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [u, setU] = useState(null);
  const [qrImg, setQrImg] = useState(null);
  const fileRef = useRef(null);
  const [upi, setUpi] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/users/${id}`);
    setU(data);
    setUpi(data.upi_id || "");
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!u) return <div className="py-20 text-center text-slate-400 animate-pulse">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="admin-user-detail">
      <button onClick={() => navigate("/admin/users")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white" data-testid="back-to-users-btn">
        <ArrowLeft className="h-4 w-4" /> Back to users
      </button>

      {/* Header */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-blue-600/10 text-blue-600 grid place-items-center font-heading font-bold text-lg">{u.name?.[0]?.toUpperCase()}</div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-xl font-bold text-slate-900 dark:text-white">{u.name}</h1>
              {u.blocked ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-600">Blocked</span> : <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">Active</span>}
            </div>
            <div className="text-sm text-slate-500">{u.email} • {u.business_name || "—"}</div>
          </div>
        </div>
        <BlockToggle u={u} onDone={load} />
      </div>

      {/* Wallets */}
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { k: "collection_balance", label: "Collection", grad: "from-blue-600 to-indigo-700" },
          { k: "payout_balance", label: "Payout", grad: "from-emerald-600 to-teal-700" },
          { k: "chargeback_balance", label: "Chargeback", grad: "from-amber-500 to-orange-600" },
        ].map((w) => (
          <div key={w.k} className={`rounded-2xl bg-gradient-to-br ${w.grad} text-white p-5`} data-testid={`detail-${w.k}`}>
            <div className="text-sm text-white/90">{w.label} Wallet</div>
            <div className="mt-3 font-mono-data text-2xl font-bold">{fmtMoney(u[w.k])}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <AddFundDialog id={id} onDone={load} />
        <TransferDialog id={id} user={u} onDone={load} />
        <PasswordDialog id={id} />
        <EditProfileDialog id={id} user={u} onDone={load} />
      </div>

      {/* QR upload */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <div className="flex items-center gap-2 mb-4"><QrCode className="h-5 w-5 text-blue-600" /><h3 className="font-heading font-semibold text-slate-900 dark:text-white">Static QR & UPI</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>UPI ID</Label>
            <Input value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="merchant@bank" data-testid="detail-upi-input" />
            <p className="text-xs text-slate-500">Current: {u.upi_id || "not set"} • QR: {u.qr_storage_path ? "uploaded ✓" : "not uploaded"}</p>
          </div>
          <div className="space-y-2">
            <Label>QR image (PNG/JPG)</Label>
            <input ref={fileRef} type="file" accept="image/*" className="block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:text-sm hover:file:bg-blue-700" data-testid="detail-qr-file" />
            <Button
              className="w-full rounded-xl bg-blue-600 hover:bg-blue-700"
              data-testid="upload-qr-submit-btn"
              onClick={async () => {
                const f = fileRef.current?.files?.[0];
                if (!upi) return toast.error("Enter UPI ID");
                if (!f) return toast.error("Select a QR image");
                const fd = new FormData();
                fd.append("upi_id", upi);
                fd.append("file", f);
                try {
                  await api.post(`/admin/users/${id}/qr`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success("QR & UPI saved");
                  load();
                } catch (err) {
                  toast.error(apiErr(err, "Upload failed"));
                }
              }}
            >
              <Upload className="h-4 w-4 mr-2" /> Upload & save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BlockToggle({ u, onDone }) {
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${u.id}/block`, { blocked: !u.blocked });
      toast.success(u.blocked ? "User unblocked" : "User blocked");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  return (
    <Button onClick={toggle} disabled={loading} variant={u.blocked ? "default" : "destructive"} className="rounded-xl" data-testid="toggle-block-btn">
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : u.blocked ? <><CheckCircle2 className="h-4 w-4 mr-2" />Unblock</> : <><Ban className="h-4 w-4 mr-2" />Block user</>}
    </Button>
  );
}

function ActionCard({ icon: Icon, label, color }) {
  return (
    <div className="flex items-center gap-2 text-sm font-medium"><Icon className={`h-4 w-4 ${color}`} /> {label}</div>
  );
}

function AddFundDialog({ id, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [utr, setUtr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{12}$/.test(utr)) return toast.error("UTR must be exactly 12 digits");
    setLoading(true);
    try {
      await api.post("/admin/payin", { user_id: id, amount: parseFloat(amount), utr });
      toast.success("Fund added to collection wallet");
      setOpen(false); setAmount(""); setUtr(""); onDone();
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:border-blue-400 transition-all text-left" data-testid="open-add-fund-btn"><ActionCard icon={Plus} label="Add fund (Pay-In)" color="text-blue-600" /></button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Add fund to collection</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Amount (₹)</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="payin-amount-input" /></div>
          <div className="space-y-1.5"><Label>UTR (12 digits)</Label><Input value={utr} onChange={(e) => setUtr(e.target.value.replace(/\D/g, "").slice(0, 12))} className="font-mono-data" placeholder="123456789012" required data-testid="payin-utr-input" /></div>
          <DialogFooter><Button type="submit" disabled={loading} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700" data-testid="payin-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to wallet"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ id, user, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState("collection_to_payout");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/transfer", { user_id: id, amount: parseFloat(amount), direction });
      toast.success("Transfer completed");
      setOpen(false); setAmount(""); onDone();
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:border-blue-400 transition-all text-left" data-testid="open-transfer-btn"><ActionCard icon={ArrowRightLeft} label="Transfer funds" color="text-emerald-600" /></button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Wallet transfer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Direction</Label>
            <div className="grid grid-cols-1 gap-2">
              <button type="button" onClick={() => setDirection("collection_to_payout")} className={`px-3 py-2 rounded-xl border text-sm text-left ${direction === "collection_to_payout" ? "border-blue-600 bg-blue-50 dark:bg-blue-950/40" : "border-slate-200 dark:border-slate-700"}`} data-testid="dir-c2p">Collection → Payout (available {fmtMoney(user.collection_balance)})</button>
              <button type="button" onClick={() => setDirection("payout_to_collection")} className={`px-3 py-2 rounded-xl border text-sm text-left ${direction === "payout_to_collection" ? "border-blue-600 bg-blue-50 dark:bg-blue-950/40" : "border-slate-200 dark:border-slate-700"}`} data-testid="dir-p2c">Payout → Collection (available {fmtMoney(user.payout_balance)})</button>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Amount (₹)</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="transfer-amount-input" /></div>
          <DialogFooter><Button type="submit" disabled={loading} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid="transfer-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Transfer"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ id }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/admin/users/${id}/password`, { password: pw });
      toast.success("Password updated");
      setOpen(false); setPw("");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:border-blue-400 transition-all text-left" data-testid="open-password-btn"><ActionCard icon={KeyRound} label="Change password" color="text-amber-600" /></button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Change password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>New password</Label><Input value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="new-password-input" /></div>
          <DialogFooter><Button type="submit" disabled={loading} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700" data-testid="password-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditProfileDialog({ id, user, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", business_name: user.business_name || "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/admin/users/${id}`, form);
      toast.success("Profile updated");
      setOpen(false); onDone();
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:border-blue-400 transition-all text-left" data-testid="open-edit-profile-btn"><ActionCard icon={QrCode} label="Edit profile" color="text-violet-600" /></button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Edit profile</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name-input" /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone-input" /></div>
          <div className="space-y-1.5"><Label>Business name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="edit-business-input" /></div>
          <DialogFooter><Button type="submit" disabled={loading} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700" data-testid="profile-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
